/**
 * @fileoverview Azure Function Health API Service
 * @description Connects the React dashboard to the portal's protected probe API.
 */

import { apiClient } from '@/services';
import { API_ENDPOINTS } from '@/services/api/endpoints';

/**
 * Extract the standardized portal data envelope.
 * @param {Object} response - Standard portal API response.
 * @returns {Object} Response payload.
 */
function unwrapResponseData(response) {
  return response?.data ?? {};
}

// Created or modified by CoPilot AI Agent
export const azureFunctionHealthApi = {
  /**
   * Load the approved catalog plus dashboard taxonomy metadata.
   * @returns {Promise<Object>} Catalog payload.
   */
  async getCatalog() {
    const response = await apiClient.get(API_ENDPOINTS.AZURE_FUNCTION_HEALTH.SCENARIOS);
    return unwrapResponseData(response);
  },

  /**
   * Load the approved Azure Function scenario catalog.
   * @returns {Promise<Array<Object>>} Catalogued health scenarios.
   */
  async getScenarios() {
    const catalog = await this.getCatalog();
    return catalog.scenarios ?? [];
  },

  /**
   * Run one approved endpoint probe through the portal backend.
   * @param {string} scenarioId - Stable server-owned scenario identifier.
   * @param {string} environment - TEST, QA, or PROD.
   * @param {string} probeKind - head or health.
   * @returns {Promise<Object>} Normalized probe result.
   */
  async probeEndpoint(scenarioId, environment, probeKind) {
    const response = await apiClient.post(API_ENDPOINTS.AZURE_FUNCTION_HEALTH.PROBE, {
      scenarioId,
      environment,
      probeKind,
    });
    return unwrapResponseData(response);
  },

  /**
   * Add or remove one endpoint-card favorite for the current user.
   * @param {number} azureFunctionId - Database ID of the endpoint card.
   * @param {boolean} isFavorite - Desired favorite state.
   * @returns {Promise<Object>} Updated favorite state.
   */
  async setFavorite(azureFunctionId, isFavorite) {
    const response = isFavorite
      ? await apiClient.post(API_ENDPOINTS.AZURE_FUNCTION_HEALTH.FAVORITES, {
        azureFunctionId,
      })
      : await apiClient.delete(
        `${API_ENDPOINTS.AZURE_FUNCTION_HEALTH.FAVORITES}/${azureFunctionId}`,
      );
    return unwrapResponseData(response);
  },

  // Created or modified by CoPilot AI Agent
  async setNotify(azureFunctionId, notify) {
    const response = await apiClient.patch(
      `${API_ENDPOINTS.AZURE_FUNCTION_HEALTH.FAVORITES}/${azureFunctionId}/notify`,
      { notify },
    );
    return unwrapResponseData(response);
  },

  // Created or modified by CoPilot AI Agent
  async setPodNotify(podId, notify) {
    const endpoint = API_ENDPOINTS.AZURE_FUNCTION_HEALTH.POD_NOTIFY;
    const response = notify
      ? await apiClient.post(endpoint, { podId })
      : await apiClient.delete(`${endpoint}/${podId}`);
    return unwrapResponseData(response);
  },
};